// Feature limitation engine for freemium model
import type { UserTier, LimitCheck, FreeTierLimits } from '../auth/types';
import type { Scale } from '../presets/presetSchema';

export class LimitationEngine {
  // Free tier limits configuration
  static readonly FREE_TIER_LIMITS: FreeTierLimits = {
    maxGuitarArrangements: 4, // Updated to match simplified freemium system - all free users get 4 guitars
    allowedScales: ['MAJOR', 'MINOR', 'PENTATONIC_MAJOR', 'PENTATONIC_MINOR', 'BLUES'],
    maxChordsInProgression: 4,
    maxSavedPresets: 5,
    allowedSoundTypes: ['ACOUSTIC'],
    overlayFeatures: false,
    exportFeatures: false,
    cagedShapes: {
      maxActiveShapes: -1, // Unlimited - CAGED shapes are free for all users
      visualizations: {
        LINK: true
      }
    }
  };

  /**
   * Check if user can add another guitar arrangement
   * SIMPLIFIED: Free users get 4 guitars, premium gets unlimited
   */
  static checkGuitarLimit(currentCount: number, tier: UserTier): LimitCheck {
    if (tier === 'premium') {
      return {
        allowed: true,
        currentCount,
        limit: -1, // Unlimited
        message: 'Premium: Unlimited arrangements'
      };
    }

    // Simple free tier check: 4 guitars max (allow exactly 4)
    const allowed = currentCount <= 4;

    return {
      allowed,
      currentCount,
      limit: 4,
      message: allowed 
        ? `${currentCount}/4 arrangements used` 
        : `Free tier limited to 4 arrangements. Upgrade for unlimited!`
    };
  }

  /**
   * Check if user has access to a specific scale
   */
  static checkScaleAccess(scale: Scale, tier: UserTier): boolean {
    if (tier === 'premium') {
      return true;
    }

    return this.FREE_TIER_LIMITS.allowedScales.includes(scale);
  }

  /**
   * Check if chord progression length is within limits
   */
  static checkChordProgressionLimit(chordCount: number, tier: UserTier): LimitCheck {
    if (tier === 'premium') {
      return {
        allowed: true,
        currentCount: chordCount,
        limit: -1,
        message: 'Premium: Unlimited chords'
      };
    }

    const limit = this.FREE_TIER_LIMITS.maxChordsInProgression;
    const allowed = chordCount <= limit;

    return {
      allowed,
      currentCount: chordCount,
      limit,
      message: allowed 
        ? `${chordCount}/${limit} chords` 
        : `Free tier limited to ${limit} chords. Upgrade for unlimited!`
    };
  }

  /**
   * Check if user can save another preset
   */
  static checkPresetLimit(currentCount: number, tier: UserTier): LimitCheck {
    if (tier === 'premium') {
      return {
        allowed: true,
        currentCount,
        limit: -1,
        message: 'Premium: Unlimited cloud presets'
      };
    }

    const limit = this.FREE_TIER_LIMITS.maxSavedPresets;
    const allowed = currentCount < limit;

    return {
      allowed,
      currentCount,
      limit,
      message: allowed 
        ? `${currentCount}/${limit} presets saved` 
        : `Free tier limited to ${limit} presets. Upgrade for unlimited cloud storage!`
    };
  }

  /**
   * Check if user has access to a specific feature
   */
  static checkFeatureAccess(feature: string, tier: UserTier): boolean {
    if (tier === 'premium') {
      return true;
    }

    // Features that are premium-only
    const premiumOnlyFeatures = [
      'overlay',
      'scaleIntersections',
      'advancedMusicTheory',
      'customTunings',
      'practiceMode',
      'export',
      'multipleAudioTypes'
    ];

    return !premiumOnlyFeatures.includes(feature);
  }

  /**
   * Check if user can use a specific sound type
   */
  static checkSoundTypeAccess(soundType: string, tier: UserTier): boolean {
    if (tier === 'premium') {
      return true;
    }

    return this.FREE_TIER_LIMITS.allowedSoundTypes.includes(soundType);
  }

  /**
   * Check CAGED shape limitations
   * Note: CAGED shapes are now unlimited for all users as they are fundamental learning tools
   */
  static checkCagedShapeLimit(activeShapeCount: number, tier: UserTier): LimitCheck {
    // CAGED shapes are unlimited for all users - they're fundamental learning tools
    return {
      allowed: true,
      currentCount: activeShapeCount,
      limit: -1,
      message: tier === 'premium' 
        ? 'Premium: All CAGED shapes available' 
        : 'All CAGED shapes available'
    };
  }

  /**
   * Get all current limitations for a user tier
   */
  static getAllLimitations(tier: UserTier): FreeTierLimits | null {
    if (tier === 'premium') {
      return null; // No limitations
    }

    return this.FREE_TIER_LIMITS;
  }

  /**
   * Get premium features list for upgrade prompts
   */
  static getPremiumFeatures(): string[] {
    return [
      'Unlimited guitar arrangements (free: 4 guitars)',
      'All scales and modes (15+ scales)',
      'Unlimited chord progressions',
      'Unlimited cloud presets with sync',
      'Advanced CAGED visualizations',
      'Scale overlay system',
      'Multiple audio types (Electric, Classical, Bass)',
      'Export capabilities',
      'Advanced music theory features',
      'Custom tunings support',
      'Practice mode with metronome'
    ];
  }

  /**
   * Generate upgrade message for a specific limitation
   */
  static getUpgradeMessage(feature: string): string {
    const messages: Record<string, string> = {
      'guitar-arrangements': 'You\'ve reached your limit of 4 guitars. Upgrade to Premium for unlimited guitar arrangements and advanced features!',
      'scales': 'Unlock all 15+ scales and modes with Premium!',
      'chord-progressions': 'Premium users can create unlimited chord progressions!',
      'presets': 'Get unlimited cloud storage for your presets with Premium!',
      'overlay': 'The scale overlay system is a Premium feature!',
      'audio-types': 'Access Electric, Classical, and Bass sounds with Premium!',
      'caged-shapes': 'All CAGED shapes are available! Upgrade for advanced visualizations and features.',
      'export': 'Export your arrangements with Premium!'
    };

    return messages[feature] || 'Upgrade to Premium to unlock this feature!';
  }

  /**
   * Get the maximum number of guitars allowed for a user tier
   * SIMPLIFIED: Free = 4, Premium = unlimited (-1)
   */
  static getMaxGuitarsForTier(tier: UserTier): number {
    return tier === 'premium' ? -1 : 4;
  }
}
